import t from "@ext/localization/locale/translate";
import SectionHeader from "@ext/settings/components/SectionHeader";
import SettingField from "@ext/settings/components/SettingField";
import { useComparePreviewEditor } from "@ext/settings/components/sections/useComparePreviewEditor";
import type { AppSettingsFormData } from "@ext/settings/logic/formSchema";
import { EditorContent, EditorContext } from "@tiptap/react";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@ui-kit/Collapsible";
import { Divider } from "@ui-kit/Divider";
import { FormStack } from "@ui-kit/Form";
import { Icon } from "@ui-kit/Icon";
import { Label } from "@ui-kit/Label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@ui-kit/Select";
import { Slider, SliderRange, SliderThumb, SliderTrack } from "@ui-kit/Slider";
import { useState } from "react";
import type { UseFormReturn } from "react-hook-form";

type SliderName =
	| "collapseThreshold"
	| "minSimilarity"
	| "debounce"
	| "minMatchLength"
	| "stopWordMinBlocks"
	| "cutDownMinWords"
	| "cutDownMinKept";

type SliderSpec = {
	name: SliderName;
	titleKey:
		| "app-settings.content-compare.collapse-threshold.title"
		| "app-settings.content-compare.min-similarity.title"
		| "app-settings.content-compare.debounce.title"
		| "app-settings.content-compare.min-match-length.title"
		| "app-settings.content-compare.stop-word-min-blocks.title"
		| "app-settings.content-compare.cut-down-min-words.title"
		| "app-settings.content-compare.cut-down-min-kept.title";
	descKey:
		| "app-settings.content-compare.collapse-threshold.description"
		| "app-settings.content-compare.min-similarity.description"
		| "app-settings.content-compare.debounce.description"
		| "app-settings.content-compare.min-match-length.description"
		| "app-settings.content-compare.stop-word-min-blocks.description"
		| "app-settings.content-compare.cut-down-min-words.description"
		| "app-settings.content-compare.cut-down-min-kept.description";
	min: number;
	max: number;
	step: number;
};

const USER_SLIDERS: readonly SliderSpec[] = [
	{
		name: "collapseThreshold",
		titleKey: "app-settings.content-compare.collapse-threshold.title",
		descKey: "app-settings.content-compare.collapse-threshold.description",
		// Zero would collapse every typo.
		min: 0.1,
		max: 1,
		step: 0.1,
	},
];

const DEVELOPER_SLIDERS: readonly SliderSpec[] = [
	{
		name: "minSimilarity",
		titleKey: "app-settings.content-compare.min-similarity.title",
		descKey: "app-settings.content-compare.min-similarity.description",
		min: 0,
		max: 1,
		step: 0.1,
	},
	{
		name: "debounce",
		titleKey: "app-settings.content-compare.debounce.title",
		descKey: "app-settings.content-compare.debounce.description",
		min: 0,
		max: 1000,
		step: 50,
	},
	{
		name: "minMatchLength",
		titleKey: "app-settings.content-compare.min-match-length.title",
		descKey: "app-settings.content-compare.min-match-length.description",
		min: 1,
		max: 10,
		step: 1,
	},
	{
		name: "stopWordMinBlocks",
		titleKey: "app-settings.content-compare.stop-word-min-blocks.title",
		descKey: "app-settings.content-compare.stop-word-min-blocks.description",
		min: 1,
		max: 64,
		step: 1,
	},
	{
		name: "cutDownMinWords",
		titleKey: "app-settings.content-compare.cut-down-min-words.title",
		descKey: "app-settings.content-compare.cut-down-min-words.description",
		min: 1,
		max: 20,
		step: 1,
	},
	{
		name: "cutDownMinKept",
		titleKey: "app-settings.content-compare.cut-down-min-kept.title",
		descKey: "app-settings.content-compare.cut-down-min-kept.description",
		min: 0.5,
		max: 1,
		step: 0.05,
	},
];

const EditorPreview = ({ form }: { form: UseFormReturn<AppSettingsFormData> }) => {
	const editor = useComparePreviewEditor(form);

	return (
		<div className="space-y-0.5 shrink-0 px-5 py-5 pt-0">
			<Label>{t("app-settings.content-compare.preview.title")}</Label>
			<div
				className="article w-full min-h-14 rounded-lg border border-secondary-border bg-secondary-bg px-3 py-2.5 shadow-soft-sm outline-none lg:py-2 font-sans text-sm"
				data-qa="content-compare-preview"
			>
				<div className="article-body">
					<EditorContext.Provider value={{ editor }}>
						<EditorContent editor={editor} />
					</EditorContext.Provider>
				</div>
			</div>
		</div>
	);
};

const SliderField = ({ name, titleKey, descKey, min, max, step }: SliderSpec) => (
	<SettingField
		control={({ field }) => (
			<div className="flex items-center gap-3 w-full">
				<Slider
					max={max}
					min={min}
					onValueChange={([v]) => field.onChange(v)}
					step={step}
					value={[Number(field.value ?? min)]}
				>
					<SliderTrack>
						<SliderRange />
					</SliderTrack>
					<SliderThumb />
				</Slider>
				<output className="text-sm tabular-nums min-w-[2.5rem] text-right">{Number(field.value ?? min)}</output>
			</div>
		)}
		description={t(descKey)}
		labelClassName="w-max"
		layout="vertical"
		name={`contentCompare.${name}` as const}
		title={t(titleKey)}
	/>
);

export const ContentCompareSection = ({ form }: { form: UseFormReturn<AppSettingsFormData> }) => {
	const [developerOpen, setDeveloperOpen] = useState(false);

	return (
		<div className="flex flex-col h-full min-h-0 gap-4">
			<div className="flex flex-1 min-h-0 flex-col gap-5 overflow-y-auto p-5">
				<SectionHeader
					description={t("app-settings.sections.content-compare.description")}
					title={t("app-settings.sections.content-compare.title")}
				/>
				<FormStack className="min-h-0">
					<SettingField
						control={({ field }) => (
							<Select onValueChange={field.onChange} value={field.value}>
								<SelectTrigger>
									<SelectValue />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="hybrid">
										{t("app-settings.content-compare.sensitivity.hybrid")}
									</SelectItem>
									<SelectItem value="character">
										{t("app-settings.content-compare.sensitivity.character")}
									</SelectItem>
									<SelectItem value="word">
										{t("app-settings.content-compare.sensitivity.word")}
									</SelectItem>
								</SelectContent>
							</Select>
						)}
						description={t("app-settings.content-compare.sensitivity.description")}
						name="contentCompare.sensitivity"
						title={t("app-settings.content-compare.sensitivity.title")}
					/>
					{USER_SLIDERS.map((spec) => (
						<SliderField key={spec.name} {...spec} />
					))}
					<Collapsible onOpenChange={setDeveloperOpen} open={developerOpen}>
						<CollapsibleTrigger asChild>
							<button
								className="flex w-full items-center gap-2 text-left text-sm"
								data-qa="content-compare-developer"
								type="button"
							>
								<Icon icon={developerOpen ? "chevron-down" : "chevron-right"} />
								<span>
									<span className="font-medium">
										{t("app-settings.content-compare.developer.title")}
									</span>
									<span className="ml-2 text-muted">
										{t("app-settings.content-compare.developer.description")}
									</span>
								</span>
							</button>
						</CollapsibleTrigger>
						<CollapsibleContent className="overflow-visible data-[state=closed]:overflow-hidden">
							<FormStack className="mt-4 min-h-0">
								{DEVELOPER_SLIDERS.map((spec) => (
									<SliderField key={spec.name} {...spec} />
								))}
							</FormStack>
						</CollapsibleContent>
					</Collapsible>
				</FormStack>
			</div>
			<Divider />
			<EditorPreview form={form} />
		</div>
	);
};
